/* eslint-disable ember/no-classic-components, ember/require-tagless-components */
import Component from "@ember/component";
import { computed, set } from "@ember/object";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { throwAjaxError } from "discourse/lib/ajax-error";
import {
  isNthPost,
  isNthTopicListItem,
} from "discourse/plugins/discourse-adplugin/discourse/helpers/slot-position";

export default class AdComponent extends Component {
  @service router;
  @service session;
  // Server needs to compute this in case hidden tags are being used.

  _impressionId = null;
  _clickTracked = false;

  _handleAdClick = () => {
    this.trackClick();
  };

  @computed(
    "router.currentRoute.attributes.category.id",
    "router.currentRoute.parent.attributes.category_id"
  )
  get currentCategoryId() {
    return (
      this.router?.currentRoute?.attributes?.category?.id ||
      this.router?.currentRoute?.parent?.attributes?.category_id
    );
  }

  @computed(
    "router.currentRoute.attributes.category.slug",
    "router.currentRoute.parent.attributes.category.slug"
  )
  get currentCategorySlug() {
    return (
      this.router?.currentRoute?.attributes?.category?.slug ||
      this.router?.currentRoute?.parent?.attributes?.category?.slug
    );
  }

  @computed("router.currentRoute.parent.attributes.tags_disable_ads")
  get topicTagsDisableAds() {
    return this.router?.currentRoute?.parent?.attributes?.tags_disable_ads;
  }

  set topicTagsDisableAds(value) {
    set(this, "router.currentRoute.parent.attributes.tags_disable_ads", value);
  }

  @computed(
    "router.currentRoute.attributes.category.read_restricted",
    "router.currentRoute.parent.attributes.category.read_restricted"
  )
  get isRestrictedCategory() {
    return (
      this.router?.currentRoute?.attributes?.category?.read_restricted ||
      this.router?.currentRoute?.parent?.attributes?.category?.read_restricted
    );
  }

  @computed("router.currentRoute.name")
  get currentRouteName() {
    return this.router?.currentRoute?.name;
  }

  set currentRouteName(value) {
    set(this, "router.currentRoute.name", value);
  }

  @computed(
    "router.currentRoute.attributes.__type",
    "router.currentRoute.attributes.id"
  )
  get topicListTag() {
    if (
      this.router?.currentRoute?.attributes?.__type === "tag" &&
      this.router?.currentRoute?.attributes?.id
    ) {
      return this.router?.currentRoute?.attributes?.id;
    }
  }

  @computed("router.currentRoute.parent.attributes.archetype")
  get isPersonalMessage() {
    return (
      this.router?.currentRoute?.parent?.attributes?.archetype ===
      "private_message"
    );
  }

  @computed
  get showToGroups() {
    if (!this.currentUser) {
      return true;
    }

    return this.currentUser.show_to_groups;
  }

  @computed(
    "currentCategoryId",
    "topicTagsDisableAds",
    "topicListTag",
    "isPersonalMessage",
    "isRestrictedCategory"
  )
  get showOnCurrentPage() {
    return (
      !this.topicTagsDisableAds &&
      (!this.currentCategoryId ||
        !this.siteSettings.no_ads_for_categories ||
        !this.siteSettings.no_ads_for_categories
          .split("|")
          .includes(this.currentCategoryId.toString())) &&
      (!this.topicListTag ||
        !this.siteSettings.no_ads_for_tags ||
        !this.siteSettings.no_ads_for_tags
          .split("|")
          .includes(this.topicListTag)) &&
      (!this.isPersonalMessage ||
        !this.siteSettings.no_ads_for_personal_messages) &&
      (!this.isRestrictedCategory ||
        !this.siteSettings.no_ads_for_restricted_categories)
    );
  }

  isNthPost(n) {
    return isNthPost(n, this.get("postNumber"));
  }

  isNthTopicListItem(n) {
    return isNthTopicListItem(n, this.get("indexNumber"));
  }

  didInsertElement() {
    super.didInsertElement?.(...arguments);

    if (this.colspan && this.element) {
      this.element.setAttribute("colspan", this.colspan);
    }

    if (!this.get("showAd")) {
      return;
    }

    this.startVisibilityTracking();
    this.startClickTracking();
  }

  startClickTracking() {
    if (!this.siteSettings.ad_plugin_enable_tracking) {
      return;
    }

    this.element.addEventListener("click", this._handleAdClick);
  }

  async trackImpression() {
    const payload = this.buildImpressionPayload?.();
    if (!payload) {
      return;
    }

    try {
      const response = await ajax("/ad_plugin/ad_impressions", {
        type: "POST",
        data: JSON.stringify(payload),
        contentType: "application/json; charset=utf-8",
      });

      this._impressionId = response.id;
    } catch (e) {
      throwAjaxError(e);
    }
  }

  trackClick() {
    if (!this._impressionId || this._clickTracked) {
      return;
    }

    this._clickTracked = true;

    const url = `/ad_plugin/ad_impressions/${this._impressionId}`;

    if (navigator.sendBeacon && !this.site?.isTesting) {
      const formData = new FormData();
      formData.append("authenticity_token", this.session.csrfToken);
      navigator.sendBeacon(url, formData);
    } else {
      ajax(url, {
        type: "PATCH",
        contentType: "application/json; charset=utf-8",
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Failed to track ad click:", e);
      });
    }
  }

  startVisibilityTracking() {
    if (!this.siteSettings.ad_plugin_enable_tracking) {
      return;
    }

    if ("IntersectionObserver" in window) {
      this._observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.trackImpression();
          this._observer.disconnect();
        }
      });
      this._observer.observe(this.element);
    } else {
      this.trackImpression();
    }
  }

  willDestroyElement() {
    super.willDestroyElement?.(...arguments);
    if (this._observer) {
      this._observer.disconnect();
    }
    if (this.element) {
      this.element.removeEventListener("click", this._handleAdClick);
    }
  }
}
